import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Alert,
  Snackbar,
  Grid
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import 'dayjs/locale/kk';
import { generateGuarantorPDF } from '../utils/generateGuarantorPDF';
import ozenLogo from '../assets/ozen.png';
import {
  Check
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { Person as PersonIcon, Badge as BadgeIcon, CreditCard as CreditCardIcon, Home as HomeIcon, LocationOn as LocationOnIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';

const API_URL = 'https://api.ozenfinance.com/api/v1/public';

const GuarantorForm = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '',
    iin: '',
    idNumber: '',
    birthDate: null,
    idIssueDate: null,
    idExpiryDate: null,
    registrationAddress: '',
    address: '',
    passport: null,
    pdfUrl: null,
    agreement: false
  });
  
  // Добавляем состояние для отслеживания шага формы
  const [formStep, setFormStep] = useState('upload'); // 'upload' или 'form'
  
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loanData, setLoanData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Получаем данные из sessionStorage
    const storedData = sessionStorage.getItem('guarantorData');
    const guarantorId = sessionStorage.getItem('guarantorId');
    const phone = sessionStorage.getItem('phone');
    const smsCode = sessionStorage.getItem('smsCode'); // Добавляем получение SMS-кода
    
    if (!guarantorId) {
      navigate('/');
      return;
    }

    if (storedData) {
    try {
        const parsedData = JSON.parse(storedData);
      setLoanData({
          id: guarantorId,
          creditRequestNumber: parsedData.creditRequestNumber,
          name: parsedData.name,
          sum: parsedData.sum,
          term: parsedData.term,
          phone: phone,
          smsCode: smsCode // Добавляем SMS-код в loanData
      });
    } catch (error) {
      console.error('Ошибка при парсинге данных:', error);
        navigate('/');
    }
    } else {
      navigate('/');
    }
  }, [navigate]);

  // Добавляем проверку наличия данных
  if (!loanData) {
    return null;
  }

  // Если форма успешно отправлена, показываем экран успеха
  if (showSuccess) {
    return (
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            padding: { xs: '24px', sm: '32px' },
            borderRadius: '16px',
            backgroundColor: 'white',
            position: 'relative',
            mt: { xs: 2, sm: 3 },
            textAlign: 'center'
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: '#4caf50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              mb: 3
            }}
          >
            <Check sx={{ color: 'white', fontSize: 40 }} />
          </Box>

          <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>
            {t('submitSuccess')}
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {t('formNote')}
          </Typography>

          <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              href="https://ozenfinance.com"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<img src={ozenLogo} alt="" style={{ width: 20, height: 20 }} />}
              sx={{
                borderRadius: '12px',
                py: 1.5,
                borderColor: '#000',
                color: '#000',
                '&:hover': {
                  borderColor: '#333',
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Özen Finance – корпоративный сайт
            </Button>
            <Button
              variant="outlined"
              href="https://ozen.cc"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<img src={ozenLogo} alt="" style={{ width: 20, height: 20 }} />}
              sx={{
                borderRadius: '12px',
                py: 1.5,
                borderColor: '#000',
                color: '#000',
                '&:hover': {
                  borderColor: '#333',
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Özen Market – маркетплейс для бизнеса
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Валидация ИИН
  const validateIIN = (iin) => {
    const iinRegex = /^\d{12}$/;
    return iinRegex.test(iin);
  };

  // Обработчик изменения полей формы
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Форматируем значение в зависимости от поля
    if (name === 'iin') {
      formattedValue = value.replace(/\D/g, '').slice(0, 12);
    } else if (name === 'idNumber') {
      formattedValue = value.replace(/\D/g, '').slice(0, 9);
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    // Очищаем ошибку при изменении поля
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Валидация формы
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName || !formData.fullName.trim()) {
      newErrors.fullName = t('fullNameRequired');
    }

    if (!validateIIN(formData.iin)) {
      newErrors.iin = t('iinInvalid');
    }

    if (!formData.idNumber || !formData.idNumber.trim()) {
      newErrors.idNumber = t('idNumberRequired');
    }

    if (!formData.birthDate) {
      newErrors.birthDate = t('birthDateRequired');
    }

    if (!formData.idIssueDate) {
      newErrors.idIssueDate = t('idIssueDateRequired');
    }

    if (!formData.idExpiryDate) {
      newErrors.idExpiryDate = t('idExpiryDateRequired');
    }

    // Адреса обязательны для отправки формы
    if (!formData.registrationAddress || !formData.registrationAddress.trim()) {
      newErrors.registrationAddress = t('registrationAddressRequired');
    }

    if (!formData.address || !formData.address.trim()) {
      newErrors.address = t('addressRequired');
    }

    // Убираем валидацию телефона, так как он приходит из сессии и мы его не меняем
    // if (loanData && loanData.phone && !validatePhone(loanData.phone)) {
    //   newErrors.phone = t('phoneInvalid');
    // }

    if (!formData.passport) {
      newErrors.passport = t('passportRequired');
    }

    if (!formData.agreement) {
      newErrors.agreement = t('agreementRequired');
    }

    // Выводим в консоль для отладки
    console.log('Исходные данные формы:', formData);
    console.log('Проверка полей формы, найденные ошибки:', newErrors);

    // Сохраняем ошибки в состоянии только если есть ошибки
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  // Показ уведомления
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Обработка загрузки файла
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsProcessing(true);
      
      // Показываем уведомление о начале обработки
      showSnackbar(t('processingDocument'), 'info');

      if (file.type === 'application/pdf') {
        // Сначала отправляем файл на OCR API для распознавания данных
        const formDataOCR = new FormData();
        formDataOCR.append('file', file);
        
        try {
          const ocrResponse = await fetch('https://ozen-ocr-api-ea18ded342a5.herokuapp.com/api/ocr/process_id_card_google', {
            method: 'POST',
            body: formDataOCR
          });
          
          if (ocrResponse.ok) {
            const ocrData = await ocrResponse.json();
            
            // Заполняем форму полученными данными
            const updatedFormData = {
              ...formData,
              passport: file,
              // Обрабатываем ФИО - склеиваем фамилию, имя и отчество
              fullName: `${ocrData.surname || ''} ${ocrData.name || ''} ${ocrData.patronymic || ''}`.trim(),
              iin: ocrData.iin || formData.iin,
              idNumber: ocrData.document_number || formData.idNumber,
              // Преобразуем строки дат в объекты dayjs если они есть
              birthDate: ocrData.birth_date ? dayjs(ocrData.birth_date) : formData.birthDate,
              idIssueDate: ocrData.issue_date ? dayjs(ocrData.issue_date) : formData.idIssueDate,
              idExpiryDate: ocrData.expiry_date ? dayjs(ocrData.expiry_date) : formData.idExpiryDate,
              // Не меняем адреса, так как они всегда вводятся вручную
              registrationAddress: formData.registrationAddress,
              address: formData.address
            };
            
            setFormData(updatedFormData);
            
            // Сбрасываем ошибки для заполненных полей
            const fieldsToReset = ['fullName', 'iin', 'idNumber', 'birthDate', 'idIssueDate', 'idExpiryDate'];
            const updatedErrors = { ...errors };
            fieldsToReset.forEach(field => {
              if (updatedErrors[field]) {
                updatedErrors[field] = null;
              }
            });
            setErrors(updatedErrors);
            
            showSnackbar(t('ocrSuccess'), 'success');
            
            // После успешного распознавания переключаем шаг на форму
            setFormStep('form');
          } else {
            // Если OCR не получилось, просто продолжаем с загрузкой файла
            console.error('OCR error:', await ocrResponse.text());
            showSnackbar(t('ocrError'), 'warning');
            // Переходим к форме даже при ошибке OCR, просто с пустыми полями
            setFormStep('form');
          }
        } catch (ocrError) {
          // Если OCR не получилось, просто продолжаем с загрузкой файла
          console.error('OCR processing error:', ocrError);
          showSnackbar(t('ocrError'), 'warning');
          // Переходим к форме даже при ошибке OCR
          setFormStep('form');
        }

        // В любом случае, сохраняем файл в формы
        setFormData(prev => ({
          ...prev,
          passport: file
        }));

        // Генерируем PDF-карточку
        const pdfBlob = await generateGuarantorPDF({
          ...formData,
          phone: loanData.phone,
          smsCode: loanData.smsCode, // Передаем SMS-код для генерации хэша
          birthDate: formData.birthDate?.format('DD.MM.YYYY'),
          idIssueDate: formData.idIssueDate?.format('DD.MM.YYYY'),
          idExpiryDate: formData.idExpiryDate?.format('DD.MM.YYYY')
        });

        const pdfUrl = URL.createObjectURL(pdfBlob);
        setFormData(prev => ({
          ...prev,
          pdfUrl
        }));

        if (errors.passport) {
          setErrors(prev => ({ ...prev, passport: null }));
        }
        
        // Если мы дошли до этого места без ошибок OCR, показываем сообщение об успешной загрузке
        if (!snackbar.open || snackbar.severity !== 'success') {
          showSnackbar(t('fileUploadSuccess'), 'success');
        }
      } else {
        showSnackbar(t('unsupportedFileType'), 'error');
        setErrors(prev => ({ 
          ...prev, 
          passport: t('unsupportedFileType') 
        }));
      }
      } catch (error) {
      console.error('Ошибка при загрузке файла:', error);
      showSnackbar(t('fileUploadError'), 'error');
      } finally {
        setIsProcessing(false);
    }
  };

  // Функция для отправки уведомления о новых данных гаранта
  const notifyGuarantorData = async (data) => {
    try {
      // Получаем актуальные данные из formData
      const payload = {
        applicationId: data.creditRequestNumber,
        fullName: formData.fullName,
        iin: formData.iin
      };
      
      // URL и заголовки для запроса
      const apiUrl = 'https://qr-code-generator-kz-4720522ef26d.herokuapp.com/api/notify-guarantor-data';
      const apiKey = process.env.REACT_APP_QR_GEN_API_KEY || 'qr_gen_sk_7f9a1b3c5d2e4f8h9j0k';

      // Проверяем, нет ли шаблонов в данных
      if (payload.fullName.includes('{{') || payload.iin.includes('{{')) {
        // В случае наличия шаблонов, используем настоящие данные
        payload.fullName = formData.fullName;
        payload.iin = formData.iin;
      }

      // Отправляем запрос через fetch
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        // Попробуем получить текст ошибки
        const errorText = await response.text();
        throw new Error(`Ошибка при отправке уведомления: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      // Последняя попытка отправки с гарантированными реальными данными
      try {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', 'https://qr-code-generator-kz-4720522ef26d.herokuapp.com/api/notify-guarantor-data');
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('X-API-Key', process.env.REACT_APP_QR_GEN_API_KEY || 'qr_gen_sk_7f9a1b3c5d2e4f8h9j0k');
        
        // Убеждаемся, что данные не содержат шаблоны
        const safePayload = {
          applicationId: data.creditRequestNumber.replace(/{{.*?}}/g, ''),
          fullName: formData.fullName.replace(/{{.*?}}/g, ''),
          iin: formData.iin.replace(/{{.*?}}/g, '')
        };
        
        xhr.send(JSON.stringify(safePayload));
        return { success: true };
      } catch (finalError) {
        return null;
      }
    }
  };

  // Отправка формы
  const handleSubmit = async () => {
    // Покажем все значения формы в консоли для отладки
    console.log('Отправка формы, данные:', {
      fullName: formData.fullName,
      iin: formData.iin,
      idNumber: formData.idNumber,
      birthDate: formData.birthDate?.format('YYYY-MM-DD'),
      idIssueDate: formData.idIssueDate?.format('YYYY-MM-DD'),
      idExpiryDate: formData.idExpiryDate?.format('YYYY-MM-DD'),
      registrationAddress: formData.registrationAddress,
      address: formData.address,
      passport: formData.passport ? 'Загружен' : 'Не загружен',
      agreement: formData.agreement
    });
    
    // Очищаем ошибки перед проверкой
    setErrors({});
    
    const formIsValid = validateForm();
    console.log('Результат валидации:', formIsValid, 'Ошибки:', errors);
    
    if (!formIsValid) {
      showSnackbar(t('formValidationError'), 'error');
      return;
    }

    try {
      setIsProcessing(true);

      // Создаем FormData для отправки файлов
      const formDataToSend = new FormData();
      formDataToSend.append('id', loanData.id);
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('iin', formData.iin);
      formDataToSend.append('docNumber', formData.idNumber);
      formDataToSend.append('birthDate', formData.birthDate?.format('YYYY-MM-DD'));
      formDataToSend.append('docIssueDate', formData.idIssueDate?.format('YYYY-MM-DD'));
      formDataToSend.append('docEndDate', formData.idExpiryDate?.format('YYYY-MM-DD'));
      formDataToSend.append('registrationAddress', formData.registrationAddress);
      formDataToSend.append('address', formData.address);

      // Добавляем файл удостоверения личности
      if (formData.passport) {
        const fileExtension = formData.passport.name.split('.').pop();
        formDataToSend.append('files', formData.passport, `${loanData.creditRequestNumber} - Удостоверение гаранта.${fileExtension}`);
      }

      // Генерируем PDF анкету и добавляем в FormData
      const pdfBlob = await generateGuarantorPDF({
        ...formData,
        phone: loanData.phone,
        smsCode: loanData.smsCode, // Передаем SMS-код для генерации хэша
        birthDate: formData.birthDate?.format('DD.MM.YYYY'),
        idIssueDate: formData.idIssueDate?.format('DD.MM.YYYY'),
        idExpiryDate: formData.idExpiryDate?.format('DD.MM.YYYY')
      });
      formDataToSend.append('files', pdfBlob, `${loanData.creditRequestNumber} - Анкета гаранта.pdf`);

      // Отправляем данные на сервер
      const response = await fetch(`${API_URL}/guarantor/data`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error('Ошибка при отправке данных');
      }

      const responseData = await response.json();
      console.log('Успешно отправлено:', responseData);
      
      // Отправляем уведомление после успешной отправки формы
      try {
        await notifyGuarantorData({
          creditRequestNumber: loanData.creditRequestNumber || 'UNKNOWN',
          // НЕ передаем здесь fullName и iin, они будут взяты из formData в функции
        });
      } catch (notifyError) {
        // Продолжаем выполнение даже при ошибке уведомления
      }
      
      setShowSuccess(true);
    } catch (error) {
      console.error('Ошибка при отправке формы:', error);
      showSnackbar(t('submitError'), 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Компонент успеха
  const SuccessScreen = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 3,
        textAlign: 'center'
      }}
    >
      {/* Анимированная иконка успеха */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: '#4caf50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          animation: 'scaleIn 0.5s ease-out',
          '@keyframes scaleIn': {
            '0%': {
              transform: 'scale(0)',
              opacity: 0
            },
            '50%': {
              transform: 'scale(1.2)',
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1
            }
          }
        }}
      >
        <Box
          component="span"
          sx={{
            color: 'white',
            fontSize: '40px',
            animation: 'checkmark 0.4s ease-in-out 0.5s forwards',
            opacity: 0,
            transform: 'scale(0)',
            '@keyframes checkmark': {
              '0%': {
                opacity: 0,
                transform: 'scale(0)'
              },
              '100%': {
                opacity: 1,
                transform: 'scale(1)'
              }
            }
          }}
        >
          ✓
        </Box>
      </Box>

      <Typography
        variant="h5"
        sx={{
          mb: 2,
          fontWeight: 500,
          animation: 'fadeIn 0.5s ease-out 0.3s forwards',
          opacity: 0,
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(10px)' },
            to: { opacity: 1, transform: 'translateY(0)' }
          }
        }}
      >
        Данные успешно отправлены
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          mb: 4,
          maxWidth: 400,
          animation: 'fadeIn 0.5s ease-out 0.5s forwards',
          opacity: 0
        }}
      >
        Ваша заявка принята в обработку. Наш менеджер свяжется с вами в ближайшее время.
      </Typography>

      <Button
        variant="contained"
        onClick={() => window.location.href = '/'}
        sx={{
          borderRadius: '12px',
          backgroundColor: '#000000',
          py: 1.5,
          px: 4,
          '&:hover': {
            backgroundColor: '#333333'
          },
          animation: 'fadeIn 0.5s ease-out 0.7s forwards',
          opacity: 0
        }}
      >
        Вернуться на главную
      </Button>
    </Box>
  );

  // Индикатор обработки
  const LoadingIndicator = () => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: '400px' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Отправка данных...
        </Typography>
        <Box 
          sx={{ 
            width: '200px',
            height: '4px',
            backgroundColor: '#e9ecef',
            borderRadius: '2px',
            mb: 3,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: '30%',
              backgroundColor: '#4CAF50',
              borderRadius: '2px',
              animation: 'loading 1.5s infinite ease-in-out',
              '@keyframes loading': {
                '0%': {
                  left: '-30%',
                },
                '100%': {
                  left: '100%',
                },
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language === 'kz' ? 'kk' : 'ru'}>
      <Box
        sx={{
      minHeight: '100vh', 
      background: 'linear-gradient(180deg, #B2C7DA 0%, #BCE3C4 100%)',
          padding: { xs: '10px', sm: '20px' }
        }}
      >
      <Container maxWidth="md">
          <Paper 
            elevation={0} 
            sx={{ 
              padding: { xs: 2, sm: 3 },
              borderRadius: '16px',
              mt: { xs: 2, sm: 3 }
            }}
          >
            {/* Заголовок формы с логотипом */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Box
                component="img"
                src={ozenLogo}
                alt="Ozen Logo"
                sx={{
                  width: '32px',
                  height: '32px',
                  objectFit: 'contain',
                  filter: 'grayscale(100%)',
                  opacity: 0.9
                }}
              />
              <Typography 
                variant="h5" 
                component="h1"
                sx={{ 
                  fontWeight: 500,
                  fontSize: '24px',
                  letterSpacing: '-0.5px'
                }}
              >
                {t('guarantorForm')}
          </Typography>
            </Box>

            {formStep === 'upload' ? (
              // Шаг 1: Только загрузка удостоверения
              <Box sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
                <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
                  Загрузите удостоверение личности для автоматического заполнения данных
            </Typography>
                
                <Box
                  sx={{
                    border: '1px dashed #000',
                    borderRadius: '12px',
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                  onClick={() => document.getElementById('passport-upload').click()}
                >
                  <input
                    id="passport-upload"
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <UploadFileIcon sx={{ fontSize: 64, color: 'text.primary', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
                    {t('uploadPassport')}
            </Typography>
                  
                  <Typography variant="body2" color="success.main" sx={{ mt: 1, mb: 2, fontWeight: 500 }}>
                    {t('autoRecognitionHint')}
                  </Typography>
                  
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                    {t('pdfOnlyMessage')}
                  </Typography>
                  
                  <Box sx={{ 
                    backgroundColor: 'rgba(0, 152, 219, 0.1)', 
                    p: 2, 
                    borderRadius: '8px',
                    mt: 2,
                    mb: 2,
                    textAlign: 'left'
                  }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#0098db', fontWeight: 'bold' }}>
                      {t('howToGetPdfTitle')}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('kaspiInstructions')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              // Шаг 2: Основная форма с автозаполненными полями и возможностью загрузить новое удостоверение
              <>
                {/* Информация о займе */}
                <Box sx={{ mb: 4, px: { xs: 2, sm: 3 } }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      mb: 2,
                      color: '#1a1a1a',
                      lineHeight: 1.5
                    }}
                    dangerouslySetInnerHTML={{
                      __html: t('infoText').replace('{name}', loanData.name).replace('{creditRequestNumber}', loanData.creditRequestNumber)
                    }}
                  />
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: '12px',
                    p: 2,
                    mb: 2
                  }}>
                    <Typography variant="body2">
                      <Box component="span" sx={{ color: '#666' }}>{t('loanAmountLabel')}</Box>
                      {' '}
                      <Box component="span" sx={{ fontWeight: 500 }}>{loanData.sum}</Box>
              </Typography>
                    <Typography variant="body2">
                      <Box component="span" sx={{ color: '#666' }}>{t('loanTermLabel')}</Box>
                      {' '}
                      <Box component="span" sx={{ fontWeight: 500 }}>{loanData.term}</Box>
              </Typography>
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#666',
                      fontStyle: 'italic'
                    }}
                  >
                    {t('formNote')}
              </Typography>
                </Box>

                <Grid container spacing={2}>
                  {/* Удостоверение личности (уже загружено) */}
                  <Grid item xs={12}>
              <Box 
                sx={{ 
                        border: '1px solid #ddd',
                  borderRadius: '12px',
                        p: 2,
                        backgroundColor: 'rgba(0, 152, 219, 0.05)',
                        mb: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <UploadFileIcon sx={{ color: 'success.main' }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {t('uploadPassport')}
                </Typography>
              </Box>
                        
                        <Button 
                          size="small" 
                          onClick={() => document.getElementById('passport-upload-change').click()}
                          sx={{ 
                            borderRadius: '8px',
                            textTransform: 'none'
                          }}
                        >
                          {t('changePassport')}
                        </Button>
                        
              <input
                          id="passport-upload-change"
                type="file"
                accept="application/pdf"
                onChange={handleFileUpload}
                          style={{ display: 'none' }}
                        />
                      </Box>
                      
                      {formData.passport && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Check fontSize="small" />
                            {formData.passport.name}
              </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
              
                  <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('fullName')}
                name="fullName"
                      value={formData.fullName}
                onChange={handleInputChange}
                      error={!!errors.fullName}
                      helperText={errors.fullName}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon />
                          </InputAdornment>
                        ),
                      }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                  }
                }}
              />
                  </Grid>

                  <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('iin')}
                name="iin"
                      value={formData.iin}
                onChange={handleInputChange}
                      error={!!errors.iin}
                      helperText={errors.iin}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeIcon />
                          </InputAdornment>
                        ),
                      }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label={t('birthDate')}
                      value={formData.birthDate}
                      onChange={(newValue) => {
                        setFormData(prev => ({ ...prev, birthDate: newValue }));
                        if (errors.birthDate) {
                          setErrors(prev => ({ ...prev, birthDate: null }));
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.birthDate,
                          helperText: errors.birthDate,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                            }
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={t('idNumber')}
                  name="idNumber"
                      value={formData.idNumber}
                  onChange={handleInputChange}
                      error={!!errors.idNumber}
                      helperText={errors.idNumber}
                  InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CreditCardIcon />
                          </InputAdornment>
                        ),
                      }}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <DatePicker
                  label={t('idIssueDate')}
                  value={formData.idIssueDate}
                      onChange={(newValue) => {
                        setFormData(prev => ({ ...prev, idIssueDate: newValue }));
                        if (errors.idIssueDate) {
                          setErrors(prev => ({ ...prev, idIssueDate: null }));
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.idIssueDate,
                          helperText: errors.idIssueDate,
                          sx: {
                    '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                            }
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <DatePicker
                  label={t('idExpiryDate')}
                  value={formData.idExpiryDate}
                      onChange={(newValue) => {
                        setFormData(prev => ({ ...prev, idExpiryDate: newValue }));
                        if (errors.idExpiryDate) {
                          setErrors(prev => ({ ...prev, idExpiryDate: null }));
                        }
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.idExpiryDate,
                          helperText: errors.idExpiryDate,
                          sx: {
                    '& .MuiOutlinedInput-root': {
                              borderRadius: '12px',
                            }
                          }
                    }
                  }}
                />
                  </Grid>

                  <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('registrationAddress')}
                name="registrationAddress"
                      value={formData.registrationAddress}
                onChange={handleInputChange}
                      error={!!errors.registrationAddress}
                      helperText={errors.registrationAddress}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <HomeIcon />
                          </InputAdornment>
                        ),
                      }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                  }
                }}
              />
                  </Grid>

                  <Grid item xs={12}>
              <TextField
                fullWidth
                      label={t('address')}
                name="address"
                      value={formData.address}
                onChange={handleInputChange}
                      error={!!errors.address}
                      helperText={errors.address}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnIcon />
                          </InputAdornment>
                        ),
                      }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                  }
                }}
              />
                  </Grid>

                  <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.agreement}
                          onChange={(e) => setFormData(prev => ({ ...prev, agreement: e.target.checked }))}
                  />
                }
                      label={t('agreementText')}
                sx={{ mb: 2 }}
              />
                    {errors.agreement && (
                      <Typography color="error" variant="caption" sx={{ mt: -2, display: 'block' }}>
                        {errors.agreement}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <LoadingButton
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                        loading={isProcessing}
                        disabled={isProcessing}
                sx={{
                  borderRadius: '12px',
                  backgroundColor: '#000000',
                  py: 1.5,
                          px: 4,
                  '&:hover': {
                    backgroundColor: '#333333'
                  }
                }}
              >
                {t('submit')}
                      </LoadingButton>
                    </Box>
                  </Grid>
                </Grid>
            </>
          )}
        </Paper>
      </Container>

        {/* Snackbar для уведомлений */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snackbar.severity}
            variant="filled"
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Индикатор загрузки */}
        {isProcessing && <LoadingIndicator />}

        {/* Экран успеха */}
        {showSuccess && <SuccessScreen />}
    </Box>
    </LocalizationProvider>
  );
};

export default GuarantorForm; 